/**
 * @fileoverview Kanban task board (MOR-696 Stage 2)
 *
 * Renders the job's (or workspace's) tasks as columns. The column dimension
 * is driven by the surface's `groupBy`:
 *   - `status`   → firm `taskStatuses` (drag persists status + manual rank)
 *   - `assignee` → org members + Unassigned (drag persists assignee + rank)
 *   - `priority` → 0–4 priority scale (drag persists priority + rank)
 *   - `label`    → firm `taskLabels` + No label (display-only grouping; a
 *                  task with multiple labels appears in each; drag reorders
 *                  within a column only)
 *   - `job`      → owning job (display-only; cross-job moves snap back)
 *   - `none`     → a single column (pure manual ordering)
 *
 * Ordering within a column uses fractional-indexing `boardRank` keys computed
 * from the dropped card's neighbours, so a move only rewrites the moved card.
 * Cross-column drops into display-only columns snap back (no field to set).
 *
 * Sub-grouping ("Then by") renders Linear-style swimlanes (MOR-707): one
 * horizontal row per sub-group value with the full column set repeated inside
 * each row, plus a per-row count and collapse toggle. Dragging a card across
 * rows reassigns the sub-group field when it is persistable.
 *
 * The board is presentational + interaction-only: data + the move callbacks
 * are passed in by the hosting route so the same board serves the job board
 * and the workspace "My tasks" surface.
 */

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import type { CalendarEventRow } from "@/calendar/eventAdapter";
import type { Id } from "@cvx/_generated/dataModel";
import type { TaskViewGroupBy } from "@cvx/tasks/taskViews/schema";
import type { BadgeColor } from "@/ui/primitives/badge";
import { TASK_COLOR_DOT } from "@/components/settings/tasks/task-colors";
import { TASK_PRIORITIES } from "@/calendar/task-priority";
import { getDisplayName } from "@/ui/composed";
import { cn } from "@/utils/misc";

import {
  TaskBoardCard,
  TaskBoardCardPreview,
  type BoardMember,
} from "./task-board-card";
import type { RowAutomation } from "@/calendar/components/automation-chip";

export interface BoardStatus {
  _id: Id<"taskStatuses">;
  name: string;
  color: BadgeColor;
  sortOrder: number;
}

export interface BoardLabel {
  _id: Id<"taskLabels">;
  name: string;
  color: BadgeColor;
}

/**
 * Result of a board drag. Exactly one grouping field (`statusId`,
 * `assigneeUserId`, or `priority`) is set when a card crosses into a
 * persistable column; a rank-only reorder leaves all three undefined.
 *
 * Persisted rows carry `id`; derived statutory rows carry `(jobId, code)`
 * instead so the mutation can materialise them on first drag (MOR-707).
 */
export interface BoardMoveArgs {
  id?: Id<"jobKeyDates">;
  jobId?: Id<"jobs">;
  code?: string;
  beforeRank: string | null;
  afterRank: string | null;
  statusId?: Id<"taskStatuses">;
  assigneeUserId?: Id<"users"> | null;
  priority?: number;
}

const NO_STATUS = "__no_status__";
const UNASSIGNED = "__unassigned__";
const NO_LABEL = "__no_label__";

/** How a drop into a given column persists (undefined ⇒ display-only). */
type ColumnDrop =
  | { kind: "status"; statusId: Id<"taskStatuses"> }
  | { kind: "assignee"; assigneeUserId: Id<"users"> | null }
  | { kind: "priority"; priority: number }
  | undefined;

interface ColumnModel {
  key: string;
  title: string;
  /** Dot colour; null ⇒ neutral dot. */
  color: BadgeColor | null;
  drop: ColumnDrop;
}

/**
 * Collision strategy tuned for kanban boards with short/empty columns.
 *
 * `closestCorners` alone makes empty or short columns hard to hit on a long
 * board: the cursor sits over the target column but the nearest *corner* is a
 * card in a taller neighbouring column. We prefer the droppable directly under
 * the pointer (so any point inside a full-height column counts), then fall back
 * to rectangle intersection, then nearest corners for fine-grained card
 * ordering.
 */
const boardCollisionDetection: CollisionDetection = (args) => {
  const pointer = pointerWithin(args);
  if (pointer.length > 0) return pointer;
  const intersections = rectIntersection(args);
  if (intersections.length > 0) return intersections;
  return closestCorners(args);
};

/** Sort comparator: manual boardRank first (when both have one), else date. */
function compareForBoard(a: CalendarEventRow, b: CalendarEventRow): number {
  if (a.boardRank && b.boardRank) return a.boardRank < b.boardRank ? -1 : 1;
  if (a.boardRank) return -1;
  if (b.boardRank) return 1;
  return a.date.localeCompare(b.date);
}

/** Column keys a card belongs to (multiple only for label grouping). */
function columnKeysFor(
  event: CalendarEventRow,
  groupBy: TaskViewGroupBy,
): string[] {
  switch (groupBy) {
    case "status":
      return [event.statusId ? String(event.statusId) : NO_STATUS];
    case "assignee":
      return [event.assigneeUserId ? String(event.assigneeUserId) : UNASSIGNED];
    case "priority":
      return [String(event.priority ?? 0)];
    case "label":
      return event.labelIds.length > 0
        ? event.labelIds.map((id) => String(id))
        : [NO_LABEL];
    case "job":
      return [String(event.jobId)];
    case "none":
      return ["__all__"];
    default: {
      const _exhaustive: never = groupBy;
      return [_exhaustive];
    }
  }
}

interface GroupContext {
  statuses: BoardStatus[];
  members: BoardMember[];
  labels: BoardLabel[];
  events: CalendarEventRow[];
}

/** Ordered column/lane definitions (with drop semantics) for a dimension. */
function groupModelsFor(
  dim: TaskViewGroupBy,
  { statuses, members, labels, events }: GroupContext,
): ColumnModel[] {
  switch (dim) {
    case "status": {
      const cols: ColumnModel[] = [
        { key: NO_STATUS, title: "No status", color: null, drop: undefined },
      ];
      // Respect the order the surface provides — it is already stage-aware
      // (custom stage order first, then status order within each stage).
      for (const s of statuses) {
        cols.push({
          key: String(s._id),
          title: s.name,
          color: s.color,
          drop: { kind: "status", statusId: s._id },
        });
      }
      return cols;
    }
    case "assignee": {
      const cols: ColumnModel[] = [
        {
          key: UNASSIGNED,
          title: "Unassigned",
          color: null,
          drop: { kind: "assignee", assigneeUserId: null },
        },
      ];
      for (const m of members) {
        cols.push({
          key: String(m._id),
          title: getDisplayName(m.firstName, m.lastName, m.email),
          color: null,
          drop: { kind: "assignee", assigneeUserId: m._id as Id<"users"> },
        });
      }
      return cols;
    }
    case "priority":
      return TASK_PRIORITIES.map((p) => ({
        key: String(p.value),
        title: p.label,
        color: p.color,
        drop: { kind: "priority", priority: p.value },
      }));
    case "label": {
      const cols: ColumnModel[] = [
        { key: NO_LABEL, title: "No label", color: null, drop: undefined },
      ];
      for (const l of labels) {
        cols.push({
          key: String(l._id),
          title: l.name,
          color: l.color,
          drop: undefined,
        });
      }
      return cols;
    }
    case "job": {
      const seen = new Map<string, string>();
      for (const e of events) {
        const key = String(e.jobId);
        if (!seen.has(key)) seen.set(key, e.jobName ?? "Job");
      }
      return Array.from(seen.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([key, title]) => ({ key, title, color: null, drop: undefined }));
    }
    case "none":
      return [
        { key: "__all__", title: "All tasks", color: null, drop: undefined },
      ];
    default: {
      const _exhaustive: never = dim;
      return _exhaustive;
    }
  }
}

export function TaskBoard({
  events,
  statuses,
  members,
  labels,
  groupBy = "status",
  subGroupBy,
  crossJob = false,
  automationsByRow,
  onCardClick,
  onMove,
}: {
  events: CalendarEventRow[];
  statuses: BoardStatus[];
  members: BoardMember[];
  labels: BoardLabel[];
  groupBy?: TaskViewGroupBy;
  /** Secondary swimlane grouping within each column; undefined ⇒ off. */
  subGroupBy?: TaskViewGroupBy;
  /** Cross-job workspace view — show the job name on each card. */
  crossJob?: boolean;
  /** Automation rules targeting each task, keyed by String(row.id). */
  automationsByRow?: ReadonlyMap<string, RowAutomation[]>;
  onCardClick: (event: CalendarEventRow) => void;
  onMove: (args: BoardMoveArgs) => void;
}) {
  // Redundant to repeat the job name when the board is already grouped by job.
  const showJobName = crossJob && groupBy !== "job";
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const memberById = useMemo(
    () => new Map(members.map((m) => [m._id, m])),
    [members],
  );
  const labelLookup = useMemo(
    () =>
      new Map(
        labels.map((l) => [String(l._id), { name: l.name, color: l.color }]),
      ),
    [labels],
  );

  // Build the ordered column definitions for the active grouping.
  const columnDefs = useMemo<ColumnModel[]>(
    () => groupModelsFor(groupBy, { statuses, members, labels, events }),
    [groupBy, statuses, members, labels, events],
  );

  // Sub-lane drop-info lookup (by sub key) for combined swimlane drags.
  const subModelByKey = useMemo(() => {
    if (!subGroupBy || subGroupBy === "none") return null;
    const models = groupModelsFor(subGroupBy, {
      statuses,
      members,
      labels,
      events,
    });
    return new Map(models.map((m) => [m.key, m]));
  }, [subGroupBy, statuses, members, labels, events]);

  // Bucket cards into their columns (multi-membership for label grouping).
  const columns = useMemo(() => {
    const byKey = new Map<string, CalendarEventRow[]>();
    for (const e of events) {
      for (const key of columnKeysFor(e, groupBy)) {
        const bucket = byKey.get(key) ?? [];
        bucket.push(e);
        byKey.set(key, bucket);
      }
    }
    for (const bucket of byKey.values()) bucket.sort(compareForBoard);

    return (
      columnDefs
        .map((def) => ({ ...def, cards: byKey.get(def.key) ?? [] }))
        // Hide the synthetic empty placeholder columns ("No status"/"No label"/
        // "Unassigned") when nothing falls into them, to reduce clutter.
        .filter(
          (col) =>
            col.drop !== undefined ||
            col.cards.length > 0 ||
            groupBy === "none",
        )
    );
  }, [columnDefs, events, groupBy]);

  // Linear-style swimlane rows (MOR-707): one row per sub-group value, the
  // shared column set repeated inside. Rows with no cards at all are hidden.
  const laneRows = useMemo(() => {
    if (!subModelByKey || !subGroupBy || subGroupBy === "none") return null;
    return Array.from(subModelByKey.values())
      .map((lane) => {
        const cells = columns.map((col) => ({
          column: col,
          cards: col.cards.filter((card) =>
            columnKeysFor(card, subGroupBy).includes(lane.key),
          ),
        }));
        const total = cells.reduce((sum, c) => sum + c.cards.length, 0);
        return { ...lane, cells, total };
      })
      .filter((row) => row.total > 0);
  }, [columns, subGroupBy, subModelByKey]);

  const [activeId, setActiveId] = useState<string | null>(null);
  // Collapsed swimlane rows, keyed by sub-group key.
  const [collapsedLanes, setCollapsedLanes] = useState<Set<string>>(
    () => new Set(),
  );
  const toggleLane = (key: string) =>
    setCollapsedLanes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const activeCard = useMemo(
    () =>
      activeId
        ? (events.find((ev) => String(ev.id) === activeId) ?? null)
        : null,
    [activeId, events],
  );

  const colByKey = useMemo(
    () => new Map(columns.map((c) => [c.key, c])),
    [columns],
  );

  const subActive = !!subGroupBy && subGroupBy !== "none";

  const handleDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;

    const dragged = events.find((ev) => String(ev.id) === String(active.id));
    // Derived rows are draggable too — they materialise on drop via (jobId,
    // code). Rows with neither a persisted id nor a code can't be moved.
    if (!dragged || (!dragged.persistedId && !dragged.code)) return;

    // Resolve the target column key + sub-lane key from the drop target.
    const overId = String(over.id);
    const {
      targetKey,
      targetSubKey,
    }: {
      targetKey: string;
      targetSubKey: string | null;
    } = (() => {
      if (overId.startsWith("lane:")) {
        const rest = overId.slice("lane:".length);
        const sep = rest.indexOf("::");
        return {
          targetKey: sep >= 0 ? rest.slice(0, sep) : rest,
          targetSubKey: sep >= 0 ? rest.slice(sep + 2) : null,
        };
      }
      if (overId.startsWith("column:")) {
        return {
          targetKey: overId.slice("column:".length),
          targetSubKey: null,
        };
      }
      const overEvent = events.find((ev) => String(ev.id) === overId);
      if (overEvent) {
        return {
          targetKey: columnKeysFor(overEvent, groupBy)[0],
          targetSubKey:
            subActive && subGroupBy
              ? columnKeysFor(overEvent, subGroupBy)[0]
              : null,
        };
      }
      return { targetKey: overId, targetSubKey: null };
    })();

    const targetCol = colByKey.get(targetKey);
    if (!targetCol) return;

    const sourceKeys = columnKeysFor(dragged, groupBy);
    const sameColumn = sourceKeys.includes(targetKey);

    // Cross-column drop into a display-only column has no field to set, so it
    // would just snap back — ignore it.
    if (!sameColumn && targetCol.drop === undefined) return;

    // Resolve the target sub-lane drop info (for swimlane reassignment).
    const targetLane =
      subActive && targetSubKey !== null && subModelByKey
        ? subModelByKey.get(targetSubKey)
        : undefined;
    const sourceSubKeys =
      subActive && subGroupBy ? columnKeysFor(dragged, subGroupBy) : [];
    const sameLane =
      targetSubKey === null || sourceSubKeys.includes(targetSubKey);

    // Build the destination order excluding the dragged card, scoped to the
    // target column (and target sub-lane when sub-grouping).
    const dest = events
      .filter((ev) => {
        if (String(ev.id) === String(active.id)) return false;
        if (!columnKeysFor(ev, groupBy).includes(targetKey)) return false;
        if (subActive && subGroupBy && targetSubKey !== null) {
          return columnKeysFor(ev, subGroupBy).includes(targetSubKey);
        }
        return true;
      })
      .sort(compareForBoard);

    // Determine insertion index: before the over-card, else append.
    const overCardIdx =
      !overId.startsWith("column:") && !overId.startsWith("lane:")
        ? dest.findIndex((ev) => String(ev.id) === overId)
        : -1;
    const insertAt = overCardIdx >= 0 ? overCardIdx : dest.length;

    const beforeRank = (() => {
      for (let i = insertAt - 1; i >= 0; i--) {
        if (dest[i].boardRank) return dest[i].boardRank;
      }
      return null;
    })();
    const afterRank = (() => {
      for (let i = insertAt; i < dest.length; i++) {
        if (dest[i].boardRank) return dest[i].boardRank;
      }
      return null;
    })();

    const base: BoardMoveArgs = {
      ...(dragged.persistedId
        ? { id: dragged.persistedId }
        : { jobId: dragged.jobId, code: dragged.code ?? undefined }),
      beforeRank,
      afterRank,
    };

    const applyDrop = (drop: ColumnDrop) => {
      if (!drop) return;
      switch (drop.kind) {
        case "status":
          base.statusId = drop.statusId;
          break;
        case "assignee":
          base.assigneeUserId = drop.assigneeUserId;
          break;
        case "priority":
          base.priority = drop.priority;
          break;
      }
    };

    // Attach the primary field change when crossing into a persistable column.
    if (!sameColumn && targetCol.drop) applyDrop(targetCol.drop);
    // Attach the secondary field change when crossing swimlanes.
    if (!sameLane && targetLane?.drop) applyDrop(targetLane.drop);

    onMove(base);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={boardCollisionDetection}
      onDragStart={(e) => setActiveId(String(e.active.id))}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={handleDragEnd}
    >
      {(() => {
        const renderCard = (card: CalendarEventRow) => (
          <TaskBoardCard
            key={String(card.id)}
            event={card}
            member={
              card.assigneeUserId
                ? (memberById.get(String(card.assigneeUserId)) ?? null)
                : null
            }
            labelLookup={labelLookup}
            showJobName={showJobName}
            automations={automationsByRow?.get(String(card.id))}
            onClick={() => {
              if (!activeId) onCardClick(card);
            }}
          />
        );

        if (laneRows) {
          // Linear-style swimlanes: a row per sub-group, columns repeated.
          return (
            <div className="flex flex-col gap-4 pb-2">
              {laneRows.map((row) => {
                const isCollapsed = collapsedLanes.has(row.key);
                return (
                  <section key={row.key}>
                    <button
                      type="button"
                      onClick={() => toggleLane(row.key)}
                      className="mb-2 flex w-full items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-muted/60"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="size-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="size-4 text-muted-foreground" />
                      )}
                      <span
                        className={cn(
                          "size-2.5 rounded-full",
                          row.color
                            ? TASK_COLOR_DOT[row.color]
                            : "bg-muted-foreground/40",
                        )}
                      />
                      <span className="truncate text-sm font-semibold">
                        {row.title}
                      </span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {row.total}
                      </span>
                    </button>

                    {!isCollapsed && (
                      <div className="flex gap-3 overflow-x-auto pb-1">
                        {row.cells.map(({ column, cards }) => (
                          <BoardCell
                            key={column.key}
                            cellId={`lane:${column.key}::${row.key}`}
                            title={column.title}
                            color={column.color}
                            count={cards.length}
                            droppable={
                              column.drop !== undefined ||
                              row.drop !== undefined
                            }
                          >
                            <SortableContext
                              items={cards.map((c) => String(c.id))}
                              strategy={verticalListSortingStrategy}
                            >
                              {cards.map(renderCard)}
                            </SortableContext>
                          </BoardCell>
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
              {laneRows.length === 0 && (
                <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                  No tasks
                </p>
              )}
            </div>
          );
        }

        return (
          <div className="flex gap-3 overflow-x-auto pb-2">
            {columns.map((col) => (
              <BoardColumn
                key={col.key}
                columnKey={col.key}
                title={col.title}
                color={col.color}
                count={col.cards.length}
                droppable={col.drop !== undefined}
              >
                <SortableContext
                  items={col.cards.map((c) => String(c.id))}
                  strategy={verticalListSortingStrategy}
                >
                  {col.cards.map(renderCard)}
                  {col.cards.length === 0 && (
                    <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                      No tasks
                    </p>
                  )}
                </SortableContext>
              </BoardColumn>
            ))}
          </div>
        );
      })()}

      <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
        {activeCard ? (
          <TaskBoardCardPreview
            event={activeCard}
            member={
              activeCard.assigneeUserId
                ? (memberById.get(String(activeCard.assigneeUserId)) ?? null)
                : null
            }
            labelLookup={labelLookup}
            showJobName={showJobName}
            automations={automationsByRow?.get(String(activeCard.id))}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// ---------------------------------------------------------------------------
// Column (droppable)
// ---------------------------------------------------------------------------

function BoardColumn({
  columnKey,
  title,
  color,
  count,
  droppable,
  children,
}: {
  columnKey: string;
  title: string;
  color: BadgeColor | null;
  count: number;
  droppable: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${columnKey}` });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-72 shrink-0 flex-col rounded-lg border border-border bg-muted/40 p-2 transition-colors",
        isOver &&
          droppable &&
          "border-primary/60 bg-primary/5 ring-2 ring-primary/50",
      )}
    >
      <div className="mb-2 flex items-center gap-2 px-1">
        <span
          className={cn(
            "size-2.5 rounded-full",
            color ? TASK_COLOR_DOT[color] : "bg-muted-foreground/40",
          )}
        />
        <span className="truncate text-sm font-semibold">{title}</span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {count}
        </span>
      </div>
      <div className="flex min-h-[3rem] flex-1 flex-col gap-2">
        {children}
        {isOver && droppable && (
          <div className="rounded-md border-2 border-dashed border-primary/50 py-3 text-center text-[11px] font-medium text-primary/70">
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell (droppable column-within-a-swimlane-row, MOR-707 Linear-style layout)
// ---------------------------------------------------------------------------

function BoardCell({
  cellId,
  title,
  color,
  count,
  droppable,
  children,
}: {
  /** Droppable id in the `lane:<columnKey>::<subKey>` scheme. */
  cellId: string;
  title: string;
  color: BadgeColor | null;
  count: number;
  droppable: boolean;
  children: React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: cellId });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex w-72 shrink-0 flex-col rounded-lg border border-border bg-muted/40 p-2 transition-colors",
        isOver &&
          droppable &&
          "border-primary/60 bg-primary/5 ring-2 ring-primary/50",
      )}
    >
      <div className="mb-2 flex items-center gap-2 px-1">
        <span
          className={cn(
            "size-2.5 rounded-full",
            color ? TASK_COLOR_DOT[color] : "bg-muted-foreground/40",
          )}
        />
        <span className="truncate text-xs font-medium text-muted-foreground">
          {title}
        </span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">
          {count}
        </span>
      </div>
      <div className="flex min-h-[3rem] flex-1 flex-col gap-2">
        {children}
        {isOver && droppable && (
          <div className="rounded-md border-2 border-dashed border-primary/50 py-3 text-center text-[11px] font-medium text-primary/70">
            Drop here
          </div>
        )}
      </div>
    </div>
  );
}
